window.__SMIND_FETCH_INSTALLED__ = window.__SMIND_FETCH_INSTALLED__ || false;
window.__SMIND_MOCK_HTML_BY_SECTION__ = window.__SMIND_MOCK_HTML_BY_SECTION__ || new Map();
window.SectionRegistry = window.SectionRegistry || {};
const loadedExternalScripts = new Set();

const AssetRegistry = {
	styles: new Map(),

	async ensureStylesLoaded(htmlString) {
		const parser = new DOMParser();
		const doc = parser.parseFromString(htmlString, 'text/html');
		const links = Array.from(doc.querySelectorAll('link[rel="stylesheet"]'));

		const promises = links.map(link => {
			const url = link.getAttribute('href');
			if (!url) return Promise.resolve();
			return this._loadStyle(url);
		});

		await Promise.all(promises);
	},

	_loadStyle(url) {
		return new Promise((resolve) => {
			const cleanUrl = url.split('?')[0];

			if (this.styles.has(cleanUrl)) {
				const entry = this.styles.get(cleanUrl);
				entry.count++;
				return resolve();
			}

			const link = document.createElement('link');
			link.rel = 'stylesheet';
			link.href = url;
			link.media = 'all';

			const entry = { count: 1, el: link, loaded: false };
			this.styles.set(cleanUrl, entry);

			link.onload = () => {
				entry.loaded = true;
				resolve();
			};
			link.onerror = () => {
				console.warn('Failed to load style:', url);
				resolve();
			};

			document.head.appendChild(link);
		});
	},

	releaseStylesIn(container) {
		const links = container.querySelectorAll('link[rel="stylesheet"]');
		links.forEach(link => {
			const url = link.getAttribute('href');
			if (!url) return;
			const cleanUrl = url.split('?')[0];

			if (this.styles.has(cleanUrl)) {
				const entry = this.styles.get(cleanUrl);
				entry.count--;

				if (entry.count <= 0) {
					if (entry.el && entry.el.parentNode) {
						entry.el.parentNode.removeChild(entry.el);
					}
					this.styles.delete(cleanUrl);
				}
			}
		});
	}
};

const RuntimeRegistry = {
	records: new Map(),
	activeSectionId: null,

	init() {
		if (this._initialized) return;
		this._initialized = true;

		const originalAdd = EventTarget.prototype.addEventListener;
		const originalSetInterval = window.setInterval;

		EventTarget.prototype.addEventListener = function (type, listener, options) {
			originalAdd.call(this, type, listener, options);
			if (RuntimeRegistry.activeSectionId) {
				RuntimeRegistry._recordEvent(RuntimeRegistry.activeSectionId, { target: this, type, listener, options });
			}
		};

		window.setInterval = function (handler, timeout, ...args) {
			const id = originalSetInterval(handler, timeout, ...args);
			if (RuntimeRegistry.activeSectionId) {
				RuntimeRegistry._recordTimer(RuntimeRegistry.activeSectionId, id);
			}
			return id;
		};
	},

	startTracking(sid) {
		this.activeSectionId = sid;
		if (!this.records.has(sid)) this.records.set(sid, { events: [], timers: [] });
	},

	stopTracking() {
		this.activeSectionId = null;
	},

	cleanup(sid) {
		const record = this.records.get(sid);
		if (!record) return;

		record.events.forEach(({ target, type, listener, options }) => {
			try { target.removeEventListener(type, listener, options); } catch (e) { }
		});

		record.timers.forEach(id => window.clearInterval(id));

		this.records.set(sid, { events: [], timers: [] });
	}

	, _recordEvent(sid, data) { const r = this.records.get(sid); if (r) r.events.push(data); }
	, _recordTimer(sid, id) { const r = this.records.get(sid); if (r) r.timers.push(id); }
};

RuntimeRegistry.init();
class SectionWrapper extends HTMLElement {
	constructor() {
		super();
		this._isUpdating = false;
		this._runtimeId = this.id || `sec_${Math.random().toString(36).substr(2, 9)}`;
	}

	connectedCallback() {
		this.setAttribute('data-section-loaded', 'true');

		this.preventLinkClicked();

		AssetRegistry.ensureStylesLoaded(this.innerHTML).then(() => {
			setTimeout(() => {
				this._setupMockFetchIfNeeded(this.innerHTML, this);

				this._runScriptsWithTracking();
				this._initSectionInstance();
				this._triggerShopifyEvent('load');
			}, 50);
		});

		this.labelNoticeForDemo();
	}

	disconnectedCallback() {
		this._cleanupSectionInstance();
		this._triggerShopifyEvent('unload');

		RuntimeRegistry.cleanup(this._runtimeId);

		AssetRegistry.releaseStylesIn(this);

		const el = this.querySelector('[section-id]');
		if (el) {
			window.__SMIND_MOCK_HTML_BY_SECTION__?.delete(el.getAttribute('section-id'));
		}
	}

	preventLinkClicked() {
		this.addEventListener("click", (e) => {
			const link = e.target.closest("a");
			if (link && this.contains(link)) {
				e.preventDefault();
				console.log("Blocked navigation:", link.href);
			}
		}, true);

		const BLOCK_ONCLICK_CLASSES = [
			"smi-collection-item-2__content"
		];

		const hasBlockedClass = (el) =>
			BLOCK_ONCLICK_CLASSES.some(cls => el.classList.contains(cls));

		this.addEventListener(
			"click",
			(e) => {
				const elWithOnclick = e.target.closest("[onclick]");
				if (!elWithOnclick) return;

				// Nếu click đang nằm trong block mà editor cần bắt (smind-block-id),
				// thì không chặn inline onclick nữa để không làm mất click selection.
				if (e.target.closest("[smind-block-id]")) return;

				if (!hasBlockedClass(elWithOnclick)) return;

				e.preventDefault();
				e.stopPropagation();
				e.stopImmediatePropagation();

				console.log("Blocked inline onclick:", elWithOnclick);
			},
			true
		);

		this.addEventListener(
			"submit",
			(e) => {
				const form = e.target;
				if (!form.checkValidity()) {
					return;
				}

				e.preventDefault();
				e.stopImmediatePropagation();

				console.log("Form hợp lệ nhưng submit đã bị chặn");
			},
			true
		);
	}

	async refresh(newHTML, sectionId = false) {
        if (this._isUpdating) return;
        this._isUpdating = true;

        // Snapshot UI state trước khi re-render: tab active, accordion open, carousel index...
        // Khi section JS init HTML mới, nó reset về tab đầu tiên — ta restore theo identifier
        // (data-id-tab, data-smind-block-id...) để user không bị "văng" về tab 1 sau khi sửa field.
        const uiStateSnapshot = this._snapshotUIState();

        this._cleanupSectionInstance();
        RuntimeRegistry.cleanup(this._runtimeId);
        this._triggerShopifyEvent('unload');

        await AssetRegistry.ensureStylesLoaded(newHTML);

        // =========================================================
        // 1. CHUẨN BỊ HTML MỚI & TƯỚC VŨ KHÍ ANIMATION 
        // =========================================================
        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = newHTML.trim();

        // Xóa các class hiệu ứng để chống việc HTML mới bay/trượt vào gây nhức mắt
        tempDiv.querySelectorAll('.smi-scroll-trigger, [class*="smi-animate"]').forEach(el => {
            el.className = el.className.replace(/smi-scroll-trigger(?:--offscreen)?|smi-animate--[\w-]+/g, '').replace(/\s+/g, ' ').trim();
            el.style.setProperty('animation', 'none', 'important');
            el.style.setProperty('transition', 'none', 'important');
            el.style.setProperty('opacity', '1', 'important');
            el.style.setProperty('transform', 'none', 'important');
        });

        this._setupMockFetchIfNeeded(newHTML, tempDiv);

        let newContent = tempDiv.firstElementChild;
        if (newContent && newContent.tagName === 'SECTION-WRAPPER') {
            newContent = newContent.firstElementChild;
        }

        if (!newContent) {
            this._isUpdating = false;
            return;
        }

        // =========================================================
        // 2. KHÓA CHIỀU CAO (CHỐNG GIẬT SCROLLBAR)
        // =========================================================
        const currentHeight = this.getBoundingClientRect().height;
        this.style.height = `${currentHeight}px`;
        this.style.overflow = 'hidden';
        if (window.getComputedStyle(this).position === 'static') {
            this.style.position = 'relative';
        }

        // =========================================================
        // 3. ĐÓNG BĂNG CÁC THẺ CŨ BẰNG XƯƠNG BẰNG THỊT (Không bọc div)
        // =========================================================
        const oldContentNodes = Array.from(this.children);
        oldContentNodes.forEach(node => {
            if (node.nodeType === 1) { 
				node.setAttribute('data-smind-dead', 'true');
                node.style.position = 'absolute';
                node.style.top = '0';
                node.style.left = '0';
                node.style.width = '100%';
                node.style.zIndex = '10'; // Đè lên che toàn bộ phía dưới
                node.style.backgroundColor = window.getComputedStyle(document.body).backgroundColor || '#ffffff';
                node.style.transition = 'opacity 0s ease-out';
                
                // Cắt đứt hoàn toàn dây mơ rễ má để JS không tìm nhầm vào thẻ cũ
                node.querySelectorAll('script').forEach(s => s.remove());
                node.querySelectorAll('[id]').forEach(el => el.removeAttribute('id'));
            }
        });

        // =========================================================
        // 4. CHÈN THẺ MỚI LÊN ĐẦU (PREPEND)
        // Kỹ thuật then chốt: Giúp JS quét từ trên xuống sẽ bắt trúng thẻ mới!
        // =========================================================
        this.prepend(newContent);

        // =========================================================
        // 5. CHẠY LẠI JAVASCRIPT
        // =========================================================
        await this._runScriptsWithTracking();
        this._initSectionInstance(); // Hàm này sẽ tự động lấy newContent (vì nó là firstElementChild)

        // Restore UI state (tab active, accordion open...) để user không bị văng về trạng thái mặc định
        this._restoreUIState(uiStateSnapshot);

        this.querySelectorAll("video").forEach(video => {
            video.load();
            if (video.hasAttribute('autoplay')) video.play();
        });

        this.dispatchEvent(new CustomEvent("section:edit", { bubbles: true }));
        if (sectionId) {
            const shopifySection = this.querySelector('.shopify-section');
            if (shopifySection) {
                shopifySection.dispatchEvent(new CustomEvent('shopify:section:load', { detail: { sectionId }, bubbles: true }));
                shopifySection.dispatchEvent(new CustomEvent('shopify:section:select', { detail: { sectionId }, bubbles: true }));
            }
        }

        setTimeout(() => {
            this.preventLinkClicked();
            this.labelNoticeForDemo();
        }, 50);

        // =========================================================
        // 6. HIỆU ỨNG CHUYỂN CẢNH (CROSSFADE) & DỌN DẸP
        // =========================================================
        requestAnimationFrame(() => {
            // Hạ Opacity của các thẻ cũ
            oldContentNodes.forEach(node => {
                if (node.nodeType === 1) node.style.opacity = '0';
            });

            // Chờ 150ms cho hiệu ứng mờ xong thì xóa sạch thẻ cũ
            setTimeout(() => {
                oldContentNodes.forEach(node => node.remove());
                this.style.height = '';
                this.style.overflow = '';
                if (this.style.position === 'relative') this.style.position = '';
                
                this._isUpdating = false;
            }, 0);
        });
    }


	async _runScriptsWithTracking() {
		RuntimeRegistry.startTracking(this._runtimeId);
		try {
			await this._executeScripts(this);
		} finally {
			setTimeout(() => RuntimeRegistry.stopTracking(), 100);
		}
	}

	async _executeScripts(container) {
		const scripts = container.querySelectorAll('script');
		for (const script of scripts) {
			if (script.type === 'application/json' || script.type === 'application/ld+json') continue;

			if (script.src) {
				if (!loadedExternalScripts.has(script.src)) {
					await this._loadExternalScript(script.src);
				}
			} else {
				const newScript = document.createElement('script');
				Array.from(script.attributes).forEach(attr => newScript.setAttribute(attr.name, attr.value));

				const safeCode = `(function(){ try { ${script.textContent} } catch(e) { console.error("Preview JS Error:", e); } })();`;
				newScript.textContent = safeCode;

				if (script.parentNode) {
					script.parentNode.replaceChild(newScript, script);
				}
			}
		}
	}

	_loadExternalScript(src) {
		return new Promise((resolve) => {
			if (loadedExternalScripts.has(src)) return resolve();
			const s = document.createElement('script');
			s.src = src;
			s.async = false;
			s.onload = () => { loadedExternalScripts.add(src); resolve(); };
			s.onerror = () => { console.error(`Failed script: ${src}`); resolve(); };
			document.head.appendChild(s);
		});
	}

	// =========================================================
	// UI STATE PRESERVATION (tab active, accordion open, etc.)
	// =========================================================
	// Vấn đề: khi refresh(), liquid thường hardcode "tab đầu active" (counter == 1)
	// khiến user đang ở tab 2 bị văng về tab 1 sau khi sửa field rightbar.
	// Giải pháp: snapshot các element đang ở trạng thái active TRƯỚC refresh
	// (keyed theo data-id-tab / smind-block-id / smind-key / index trong parent),
	// sau khi script init xong thì tìm lại element tương ứng trong DOM mới và
	// .click() để section JS tự đồng bộ state một cách tự nhiên.
	_snapshotUIState() {
		const snapshots = [];
		const DEBUG_UI_STATE = true;
		const STATE_CLASSES = ['active', 'selected', 'open', 'expanded', 'current', 'is-active', 'is-open'];
		const CLICKABLE_SELECTORS = [
			'[data-id-tab]',
			'[role="tab"]',
			'.smi-item-tab',
			'.smi-tab-button',
			'.smi-tab__button',
			'.smi-accordion-header',
			'.smi-accordion-trigger',
			'[data-accordion-trigger]',
			'[data-tab-trigger]',
		];

		const hasStateClass = (el) => {
			const tokens = (el.className || '').toString().split(/\s+/);
			return STATE_CLASSES.some(c => tokens.includes(c));
		};

		CLICKABLE_SELECTORS.forEach((sel) => {
			let nodes;
			try { nodes = this.querySelectorAll(sel); } catch (e) { return; }
			nodes.forEach((el) => {
				if (!hasStateClass(el)) return;

				const dataIdTab = el.getAttribute('data-id-tab');
				const blockId = el.getAttribute('smind-block-id');
				const smindKey = el.getAttribute('smind-key');

				const siblings = el.parentElement
					? Array.from(el.parentElement.querySelectorAll(sel)).filter(n => n.parentElement === el.parentElement)
					: [];
				const index = siblings.indexOf(el);

				snapshots.push({
					selector: sel,
					dataIdTab: dataIdTab || null,
					blockId: blockId || null,
					smindKey: smindKey || null,
					index: index >= 0 ? index : -1,
				});
			});
		});

		if (DEBUG_UI_STATE) console.log('[UIState] snapshot', this.id, snapshots);
		return snapshots;
	}

	_restoreUIState(snapshots) {
		const DEBUG_UI_STATE = true;
		if (!Array.isArray(snapshots) || !snapshots.length) {
			if (DEBUG_UI_STATE) console.log('[UIState] restore SKIPPED - empty snapshots', this.id);
			return;
		}
		if (DEBUG_UI_STATE) console.log('[UIState] restore START', this.id, snapshots);

		const STATE_CLASSES = ['active', 'selected', 'open', 'expanded', 'current', 'is-active', 'is-open'];
		const hasStateClass = (el) => {
			const tokens = (el.className || '').toString().split(/\s+/);
			return STATE_CLASSES.some(c => tokens.includes(c));
		};

		const safeEscape = (v) => (window.CSS && CSS.escape ? CSS.escape(v) : String(v).replace(/"/g, '\\"'));

		const pickFresh = (nodeList) => {
			for (const el of nodeList) {
				if (el.closest('[data-smind-dead="true"]')) continue;
				return el;
			}
			return null;
		};

		const findTarget = (snap) => {
			try {
				if (snap.dataIdTab) {
					const t = pickFresh(this.querySelectorAll(`${snap.selector}[data-id-tab="${safeEscape(snap.dataIdTab)}"]`));
					if (t) return t;
				}
				if (snap.blockId) {
					const t = pickFresh(this.querySelectorAll(`${snap.selector}[smind-block-id="${safeEscape(snap.blockId)}"]`));
					if (t) return t;
				}
				if (snap.smindKey) {
					const t = pickFresh(this.querySelectorAll(`${snap.selector}[smind-key="${safeEscape(snap.smindKey)}"]`));
					if (t) return t;
				}
				if (snap.index >= 0) {
					const all = this.querySelectorAll(snap.selector);
					const fresh = Array.from(all).filter(n => !n.closest('[data-smind-dead="true"]'));
					return fresh[snap.index] || null;
				}
			} catch (e) { /* selector invalid */ }
			return null;
		};

		// Áp active class TRỰC TIẾP cho tab (không phụ thuộc tab handler đã bind chưa).
		// Làm cả 2 việc: (1) apply class ngay → UI đúng ngay cả khi smi-base tabInit chưa xong,
		// (2) click element để trigger side effects (slider init, scroll-into-view, ...).
		const directlyApplyTabActive = (target) => {
			const dataId = target.getAttribute('data-id-tab');
			if (!dataId) return false;

			// Scope = section outer chứa tab list + tab contents
			const scope = target.closest('.smi-section, .shopify-section, section-wrapper') || this;

			// 1. Xóa active khỏi tất cả tab button siblings trong scope
			scope.querySelectorAll('.smi-item-tab, [role="tab"]').forEach(el => el.classList.remove('active'));
			// 2. Xóa active khỏi tất cả tab content trong scope
			scope.querySelectorAll('.smi-content-tab').forEach(el => el.classList.remove('active'));
			// 3. Xóa active khỏi button view-all tương ứng (theo smi-base logic)
			scope.querySelectorAll('.smi-button-tab .view-all').forEach(el => el.classList.remove('active'));

			// 4. Add active cho target button
			target.classList.add('active');

			// 5. Add active cho tab content tương ứng (match by id = data-id-tab)
			const content = document.getElementById(dataId);
			if (content && !content.closest('[data-smind-dead="true"]')) {
				content.classList.add('active');
			}

			// 6. Add active cho button data-id-button (nếu có)
			try {
				const viewAllBtn = document.querySelector(`[data-id-button="${safeEscape(dataId)}"]`);
				if (viewAllBtn && !viewAllBtn.closest('[data-smind-dead="true"]')) {
					viewAllBtn.classList.add('active');
				}
			} catch (e) { /* selector invalid */ }

			return true;
		};

		// Chờ 1 frame cho DOM settle rồi apply. Direct class apply không cần handler bind.
		const apply = (tag) => {
			snapshots.forEach((snap) => {
				const target = findTarget(snap);
				if (DEBUG_UI_STATE) console.log('[UIState] apply', tag, 'snap=', snap, 'target=', target, 'hasState=', target && hasStateClass(target));
				if (!target) return;
				if (hasStateClass(target)) return;

				const appliedAsTab = directlyApplyTabActive(target);
				if (DEBUG_UI_STATE) console.log('[UIState] directApply=', appliedAsTab, 'after hasState=', hasStateClass(target));

				try { target.click(); } catch (e) { /* ignore */ }
			});
		};

		// Apply NGAY (sync) — để tab/content mới có đúng active trước khi crossfade reveal.
		// Nếu delay, user sẽ thấy tab 1 default active flash lên trong ~150ms → cảm giác "nhảy".
		apply('sync');
		// Backup retries phòng trường hợp smi-base tabInit chạy sau và làm reset.
		setTimeout(() => apply('t100'), 100);
		setTimeout(() => apply('t400'), 400);
	}

	_initSectionInstance() {
		const container = this.firstElementChild;
		if (!container) return;

		const type = container.dataset.sectionType || container.id;
		if (!type) return;

		const Constructor = window.SectionRegistry[type];
		if (Constructor) {
			try {
				this.__sectionInstance = new Constructor(container);
			} catch (e) {
				console.error('Section ctor error', e);
			}
		}
	}

	_cleanupSectionInstance() {
		if (this.__sectionInstance?.unload) {
			try { this.__sectionInstance.unload(); } catch (e) { }
		}
		this.__sectionInstance = null;
	}

	_setupMockFetchIfNeeded(html, container) {
		let targetElement =
			container?.hasAttribute?.('section-id')
				? container
				: container?.querySelector?.('[section-id]');

		if (!targetElement) return;

		const sectionId = targetElement.getAttribute('section-id');

		if (
			!sectionId ||
			(!sectionId.includes('smi_featured_product') &&
				!sectionId.includes('smi_product_detail') &&
				!sectionId.includes('smi_related_products'))
		) return;

		window.__SMIND_MOCK_HTML_BY_SECTION__.set(sectionId, html);

		if (window.__SMIND_FETCH_INSTALLED__) return;


		window.__SMIND_FETCH_INSTALLED__ = true;

		const originalFetch = window.fetch;

		window.fetch = async (input, options) => {
			const urlStr = typeof input === 'string' ? input : input?.url || '';

			try {
				const parsedUrl = new URL(urlStr, window.location.origin);

				const intent = parsedUrl.searchParams.get('intent');
				const sectionIdParam = parsedUrl.searchParams.get('section_id');

				if (
					(intent === 'complementary' || intent === 'related') &&
					sectionIdParam &&
					window.__SMIND_MOCK_HTML_BY_SECTION__.has(sectionIdParam)
				) {
					const mockHtml = window.__SMIND_MOCK_HTML_BY_SECTION__.get(sectionIdParam);

					return new Response(
						`<div data-mock="true">${mockHtml}</div>`,
						{
							status: 200,
							headers: { 'Content-Type': 'text/html' },
						}
					);
				}
			} catch (e) {
				console.warn('Mock fetch parse error', e);
			}

			return originalFetch(input, options);
		};
	}


	_triggerShopifyEvent(type) {
		const target = this.querySelector('.shopify-section') || this.firstElementChild;
		if (!target) return;
		const event = new CustomEvent(`shopify:section:${type}`, {
			bubbles: true,
			detail: { sectionId: target.getAttribute('id') || this.id }
		});
		target.dispatchEvent(event);
	}

	labelNoticeForDemo() {

		const tooltipWrp = document.querySelector('.smi-demo-tooltip-wrapper');
		if (!!tooltipWrp) {
			tooltipWrp.remove();
		}

		const tooltip = document.createElement('div');
		tooltip.className = 'smi-demo-tooltip-wrapper';
		tooltip.innerText = 'Demo only for live preview';
		Object.assign(tooltip.style, {
			position: 'fixed', padding: '8px 12px', background: 'rgba(0,0,0,0.9)',
			color: '#fff', fontSize: '12px', borderRadius: '4px', pointerEvents: 'none',
			zIndex: '2147483647', whiteSpace: 'nowrap', opacity: '0',
			transition: 'opacity 0.15s ease-out, transform 0.15s ease-out',
			transform: 'translate(-50%, -100%)', top: '0', left: '0'
		});
		const arrow = document.createElement('div');
		Object.assign(arrow.style, {
			position: 'absolute', bottom: '-6px', left: '50%', transform: 'translateX(-50%)',
			width: '0', height: '0', borderLeft: '6px solid transparent',
			borderRight: '6px solid transparent', borderTop: '6px solid rgba(0,0,0,0.9)'
		});
		tooltip.appendChild(arrow);
		document.body.appendChild(tooltip);

		const show = (e) => {
			const rect = e.target.getBoundingClientRect();
			tooltip.style.left = `${rect.left + rect.width / 2}px`;
			tooltip.style.top = `${rect.top - 17}px`;
			tooltip.style.opacity = '1';
			tooltip.style.transform = 'translate(-50%, -100%)';
		};
		const hide = () => { tooltip.style.opacity = '0'; tooltip.style.transform = 'translate(-50%, -90%)'; };
		const selectors = [
			'.smi-component-blocks-follow-on-shop',
			'.smi-footer-content-end .lang-con-footer',
			'.smi-footer-content-end .smi-policies',
			'.smi-footer-policies',
			'.smi-sidebar .smi-block-tags ul',
			'.smi-article__navigation ._previous',
			'.smi-article__navigation ._next',
			'.smi-article__comment',
			'.smi-pagination-default',
			'smi-cp-amount',
			'.smi-sorter-wrap',
			'.smi-section-collection-page-2 smi-cp-filter'
		];
		selectors.forEach(sel => this.querySelectorAll(sel).forEach(el => {
			el.addEventListener('mouseenter', show);
			el.addEventListener('mouseleave', hide);
		}));
	}
}

if (!customElements.get('section-wrapper')) {
	customElements.define('section-wrapper', SectionWrapper);
}